import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import Grid from '@material-ui/core/Grid';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import Section from 'core/components/section';
import CdDiffSummary from './cd-diff-summary';
import DiffSummary from './diff-summary';
import useStyles from './preview-clariondoor-request-diff.styles';
import { buildBranchDiffRows } from './branch-data-diff';
import { getSegmentDetailsForCdRequest } from './cd-vehicle-labels';
import { GET_CD_VEHICLE_LABELS } from './cd-vehicle-labels.queries';

const BRANCH_DATA_SECTION = { id: 'branch-data', label: 'Branch Data Changes' };
const CD_SECTION = { id: 'cd-changes', label: 'ClarionDoor Changes' };

const VIEWS = { SUMMARY: 'summary', RAW: 'raw' };

const getInitialAccumulatorValue = (removedChanges, addedChanges) => {
  const obj = {};
  Object.keys(removedChanges).forEach((key) => {
    obj[key] = null;
  });
  Object.keys(addedChanges).forEach((key) => {
    obj[key] = null;
  });
  return obj;
};

const sortedObj = (obj, defaults) =>
  Object.keys(obj)
    .sort()
    .reduce((accumulator, key) => {
      accumulator[key] = obj[key];
      return accumulator;
    }, defaults);

const PreviewClarionDoorRequestDifference = ({
  policyPreviewId,
  policyId,
  accountId,
  beforeCars,
  afterCars,
  cdIdBefore,
  cdIdAfter,
  hideBranchDataDiff = false,
  beforeTitle,
  afterTitle
}) => {
  const classes = useStyles();
  const {
    account: {
      policies: {
        policy: { getClarionDoorRequestDiff }
      }
    }
  } = useStore();

  const [data, setData] = useState();
  const [gqlError, setGqlError] = useState();
  const [loading, setLoading] = useState(false);
  const [branchView, setBranchView] = useState(VIEWS.SUMMARY);
  const [cdView, setCdView] = useState(VIEWS.SUMMARY);

  // Two fetch modes:
  // - cdId path (version-compare): diff two arbitrary ClarionDoor request IDs. No
  //   Branch-data diff is produced on this path, so the caller sets hideBranchDataDiff.
  // - preview path (policy preview): diff the before/after of a single preview.
  const useCdIdPath = Boolean(cdIdBefore && cdIdAfter);
  const { data: vehicleLabels } = useQuery(GET_CD_VEHICLE_LABELS, {
    variables: { policyId, accountId, previewId: policyPreviewId },
    skip: useCdIdPath || !policyId || !accountId || !policyPreviewId,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all'
  });
  const previewBeforeDetails = useMemo(
    () => getSegmentDetailsForCdRequest(vehicleLabels?.before, data?.requestIdBeforePreview),
    [vehicleLabels, data]
  );
  const previewAfterDetails = useMemo(
    () => getSegmentDetailsForCdRequest(vehicleLabels?.after?.policy, data?.requestIdAfterPreview),
    [vehicleLabels, data]
  );
  const branchDiffRows = useMemo(
    () =>
      data?.diff &&
      buildBranchDiffRows({
        ...data.diff.full,
        beforeDetails: previewBeforeDetails,
        afterDetails: previewAfterDetails
      }),
    [data, previewBeforeDetails, previewAfterDetails]
  );

  // Guard on gqlError too: on error we set gqlError but never set data, so without this the
  // render-time fetch would re-fire every render and hammer the backend in an infinite loop.
  if (!loading && !data && !gqlError) {
    setLoading(true);
    const args = useCdIdPath
      ? { cdIdBefore, cdIdAfter, invokedBy: 'staff-fe-version-compare' }
      : { policyId, policyPreviewId };
    getClarionDoorRequestDiff(args)
      .then(({ requests, error }) => {
        setLoading(false);
        if (requests) {
          // `diff` (Branch-data changes) is only populated on the preview path; it's
          // null on the cdId path. `{}` is not valid JSON, so guard before parsing.
          setData({ ...requests, diff: requests?.diff ? JSON.parse(requests.diff) : null });
        }
        if (error) {
          setGqlError(error);
        }
      })
      .catch((e) => {
        // The store action re-throws on Apollo/network errors; without this the spinner
        // would hang forever with no error surfaced.
        setLoading(false);
        setGqlError(e?.message ?? 'Unexpected error loading ClarionDoor diff');
      });
  }

  const sortedRemovedChanges =
    data?.diff &&
    sortedObj(
      data?.diff.full.removedChanges,
      getInitialAccumulatorValue(data?.diff.full.removedChanges, data?.diff.full.addedChanges)
    );
  const sortedAddedChanges =
    data?.diff &&
    sortedObj(
      data?.diff.full.addedChanges,
      getInitialAccumulatorValue(data?.diff.full.removedChanges, data?.diff.full.addedChanges)
    );

  if (loading) {
    return <Loading />;
  }

  const hasRequestXml = data?.xmlBeforePreview?.success && data?.xmlAfterPreview?.success;

  const sections = hideBranchDataDiff ? [CD_SECTION] : [BRANCH_DATA_SECTION, CD_SECTION];

  const leftXmlTitle = beforeTitle ?? 'ClarionDoor Request Before Preview';
  const rightXmlTitle = afterTitle ?? 'ClarionDoor Request After Preview';

  const viewTabs = ({ view, setView }) => (
    <Tabs
      value={view}
      onChange={(_, nextView) => setView(nextView)}
      indicatorColor="secondary"
      classes={{ root: classes.viewTabs, flexContainer: classes.viewTabsContainer }}
    >
      <Tab value={VIEWS.SUMMARY} label="Summary" className={classes.viewTab} />
      <Tab value={VIEWS.RAW} label="Raw" className={classes.viewTab} />
    </Tabs>
  );
  const branchTabs = viewTabs({ view: branchView, setView: setBranchView });
  const cdTabs = viewTabs({ view: cdView, setView: setCdView });

  return (
    <>
      {/* The "Jump to" nav only earns its keep when there's more than one section to jump between.
          On the version-compare path there's a single section, so the nav would just anchor to the
          content directly below it — hide it there. */}
      {sections.length > 1 && (
        <Grid container spacing={2} className={classes.navBar}>
          <Grid item>
            <Label type="darkGreenSmallBold" className={classes.jumpToLabel}>
              Jump to:
            </Label>
          </Grid>
          {sections.map(({ id, label }) => (
            <Grid item key={id}>
              <Button variant="contained" color="secondary" href={`#${id}`}>
                {label}
              </Button>
            </Grid>
          ))}
        </Grid>
      )}

      {!hideBranchDataDiff && (
        <Section title="Branch Data Changes" type="Section" id="branch-data">
          {data?.diff && branchView === VIEWS.SUMMARY && (
            <DiffSummary rows={branchDiffRows} header={branchTabs} emptyMessage="No Branch data differences" />
          )}
          {data?.diff && branchView === VIEWS.RAW && (
            <>
              {branchTabs}
              <div className={classes.xmlDiff}>
                <ReactDiffViewer
                  oldValue={JSON.stringify(sortedRemovedChanges, null, 4)}
                  newValue={JSON.stringify(sortedAddedChanges, null, 4)}
                  splitView
                  compareMethod={DiffMethod.WORDS}
                  leftTitle={'Branch Data Before Preview'}
                  rightTitle={'Branch Data After Preview'}
                />
              </div>
            </>
          )}
          {!data?.diff && (
            <>
              <Label>Error getting Branch Data diff.</Label>
              {gqlError && <Label>{gqlError}</Label>}
            </>
          )}
        </Section>
      )}

      <Section title="ClarionDoor Changes" type="Section" id={CD_SECTION.id}>
        {(!hasRequestXml || cdView !== VIEWS.SUMMARY) && cdTabs}

        {!hasRequestXml && (
          <>
            <Label>Error getting ClarionDoor request data.</Label>
            <Label>{data?.xmlBeforePreview?.message || data?.xmlAfterPreview?.message || gqlError}</Label>
          </>
        )}

        {hasRequestXml && cdView === VIEWS.SUMMARY && (
          <CdDiffSummary
            key={`${data.requestIdBeforePreview}:${data.requestIdAfterPreview}`}
            header={cdTabs}
            beforeRequestXml={data.xmlBeforePreview.data}
            afterRequestXml={data.xmlAfterPreview.data}
            beforeCars={beforeCars ?? previewBeforeDetails.cars ?? []}
            afterCars={afterCars ?? previewAfterDetails.cars ?? []}
          />
        )}

        {hasRequestXml && cdView === VIEWS.RAW && (
          <Section title="ClarionDoor Request XML" type="SubSection" id="cd-request">
            <div className={classes.xmlDiff}>
              <ReactDiffViewer
                oldValue={data.xmlBeforePreview.data}
                newValue={data.xmlAfterPreview.data}
                splitView
                compareMethod={DiffMethod.WORDS}
                leftTitle={`${leftXmlTitle} - ID: ${data?.requestIdBeforePreview}`}
                rightTitle={`${rightXmlTitle} - ID: ${data?.requestIdAfterPreview}`}
              />
            </div>
          </Section>
        )}
      </Section>
    </>
  );
};

export default PreviewClarionDoorRequestDifference;
